import React from "react";
import { useLocation, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { http, HttpError } from "@/lib/http";
import { useAuth } from "@/app/AuthContext";
import { useT } from "@/app/localization";
import { errorMessage } from "@/app/serverErrors";

function Spinner({ className }: { className: string }) {
  const t = useT();
  return (
    <div role="status" className={className}>
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <span className="sr-only">{t("common.state.loading")}</span>
    </div>
  );
}

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const t = useT();
  const location = useLocation();

  const q = useQuery({
    queryKey: ["auth", "check"],
    queryFn: async () => {
      return http<any>("/api/auth/me");
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  if (q.isLoading) {
    return <Spinner className="h-screen w-screen grid place-items-center" />;
  }

  if (q.isError) {
    const err = q.error as unknown;
    if (err instanceof HttpError && (err.status === 401 || err.status === 403)) {
      return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return (
      <div className="h-screen w-screen grid place-items-center p-6">
        <div className="rounded-xl bg-background shadow p-6">
          <div className="font-semibold">{t("operator.shell.auth_check_failed")}</div>
          <div className="mt-2 text-sm opacity-70">{errorMessage(t, err, { fallback: "operator.error.request_failed" })}</div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

// System-admin pages. A full admin OR a read-only admin (auditor) may VIEW them;
// write controls inside are hidden for the read-only admin and, ultimately, the
// backend rejects any write it attempts. Use RequireFullAdmin for pages whose
// whole purpose is mutation (e.g. the emulator).
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdminViewer, isLoading } = useAuth();

  if (isLoading) {
    return <Spinner className="flex justify-center p-12" />;
  }

  if (!isAdminViewer) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

// Full-admin-only pages: a read-only admin is sent to /forbidden. For surfaces
// that exist only to mutate state (the emulator proxy is full-admin-only on the
// backend too).
export function RequireFullAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useAuth();

  if (isLoading) {
    return <Spinner className="flex justify-center p-12" />;
  }

  if (!isAdmin) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}

// Organization-MANAGEMENT pages (organizations, access control, special rates):
// a system admin OR an organization manager. A restricted driver — an org member
// who is not a manager — is sent to /forbidden. Backend authorization is
// authoritative; this guard only keeps the driver out of management UI.
export function RequireAdminOrOrg({ children }: { children: React.ReactNode }) {
  const { isAdmin, isManager, isLoading } = useAuth();

  if (isLoading) {
    return <Spinner className="flex justify-center p-12" />;
  }

  if (!isAdmin && !isManager) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
}
